import React, { useCallback, useEffect } from "react";

import moment from "moment";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { NumberParam, StringParam, useQueryParam, useQueryParams, withDefault } from "use-query-params";

import { Table } from "antd";
import { SorterResult } from "antd/lib/table/interface";

import { Page } from "../../components/page";
import { PageHeader } from "../../components/page_header";
import { Searcher } from "../../components/searcher";
import { CurrencySelector, DateRange, MarketSelector } from "../../components/selectors";
import { ColumnTitle, TableNotification } from "../../components/table";
import { iataFetchRequest, reportGeneratorTaskCreateRequest, tableFetchRequest } from "../../store/auction/actions";
import { AuctionItem } from "../../store/auction/types";
import { ReportFormat } from "../../store/common_types";
import { selectCurrency } from "../../store/currencies/actions";
import { RootState } from "../../store/root_reducer";
import { MomentParam } from "../../utils/moment_param";
import { formatNumber, getSortOrder } from "../../utils/table";

export const AuctionPage: React.FC = () => {
  const dispatch = useDispatch();

  // -- Hooks Start --
  const [dateRange, setDateRange] = useQueryParams({
    from: withDefault(MomentParam, moment.utc().startOf("day").subtract(1, "week").add(1, "day")),
    to: withDefault(MomentParam, moment.utc().startOf("day")),
  });
  const [pagination, setPagination] = useQueryParams({
    page: withDefault(NumberParam, 1),
    pageSize: withDefault(NumberParam, 10),
  });
  const [market, setMarket] = useQueryParam("market", withDefault(StringParam, undefined));
  const [search, setSearch] = useQueryParam("search", withDefault(StringParam, undefined));
  const [ordering, setOrdering] = useQueryParam("ordering", withDefault(StringParam, "-avgStr"));
  // -- Hooks End --

  // -- Stores Start --
  const reportState = useSelector((state: RootState) => state.Auction);
  const markets = useSelector((state: RootState) => state.Markets.markets);
  const selectedGate = useSelector((state: RootState) => state.User.selectedGate);
  const currency = useSelector((state: RootState) => state.Currencies.selectedCurrency);
  // -- Stores End --

  // -- Custom Handlers Start --
  const onExpand = (iata: string) => {
    if (!(iata in reportState.itemsByIata)) {
      dispatch(
        iataFetchRequest({
          ...dateRange,
          iata,
          market,
          currency,
          gateId: selectedGate.id,
        })
      );
    }
  };
  const chooseCurrency = (value: string) => dispatch(selectCurrency({ currency: value }));
  const onDateRangeSelect = (value: DateRange) => {
    setDateRange(value);
    setPagination({ ...pagination, page: 1 }, "replaceIn");
  };
  // -- Custom Handlers End --

  // -- Hook Effects Start --
  useEffect(() => {
    setDateRange((state) => state, "replaceIn");
    setPagination((state) => state, "replaceIn");
    setMarket((state) => state, "replaceIn");
    setSearch((state) => state, "replaceIn");
    setOrdering((state) => state, "replaceIn");
  }, [setDateRange, setMarket, setOrdering, setPagination, setSearch]);

  const fetch = useCallback(() => {
    dispatch(
      tableFetchRequest({
        ...pagination,
        ...dateRange,
        market,
        currency,
        ordering,
        search,
        gateId: selectedGate.id,
      })
    );
  }, [dispatch, dateRange, pagination, market, currency, ordering, selectedGate, search]);

  const downloadReport = useCallback(
    (reportFormat: ReportFormat) => {
      dispatch(
        reportGeneratorTaskCreateRequest({
          ...dateRange,
          market,
          currency,
          ordering,
          reportFormat,
          gateId: selectedGate.id,
        })
      );
    },
    [dispatch, market, currency, ordering, dateRange, selectedGate]
  );

  useEffect(() => {
    fetch();
  }, [fetch]);
  // -- Hook Effects End --

  const { t } = useTranslation(["auction_page", "common"]);
  const columns = [
    {
      title: <ColumnTitle title={t("table.columns.iata.title")} helpText={t("table.columns.iata.help")} />,
      key: "iata",
      dataIndex: "iata",
      sorter: true,
      sortOrder: getSortOrder("iata", ordering),
      expandable: true,
    },
    {
      title: <ColumnTitle title={t("table.columns.avgStr.title")} helpText={t("table.columns.avgStr.help")} />,
      key: "avgStr",
      dataIndex: "avgStr",
      render: (avgStr: number) => formatNumber(avgStr),
      sorter: true,
      sortOrder: getSortOrder("avgStr", ordering),
    },
    {
      title: (
        <ColumnTitle title={t("table.columns.avgCommission.title")} helpText={t("table.columns.avgCommission.help")} />
      ),
      key: "avgCommission",
      dataIndex: "avgCommission",
      render: (avgCommission: number) => formatNumber(avgCommission),
      sorter: true,
      sortOrder: getSortOrder("avgCommission", ordering),
    },
    {
      title: <ColumnTitle title={t("table.columns.avgPrice.title")} helpText={t("table.columns.avgPrice.help")} />,
      key: "avgPrice",
      dataIndex: "avgPrice",
      sortOrder: getSortOrder("avgPrice", ordering),
      render: (avgPrice: number) => formatNumber(avgPrice),
      sorter: true,
    },
  ];

  const dataSource = reportState.items;
  if (!reportState.isLoading && reportState.items.length > 0) {
    for (const item of dataSource) {
      item.items = reportState.itemsByIata[item.iata] || [];

      for (const subItem of item.items) {
        subItem.iata = subItem.createdAt;
      }
    }
  }

  const handleSearch = (value: string) => {
    setSearch(value || undefined);
    setPagination((state) => ({ ...state, page: 1 }));
  };

  return (
    <Page title={t("title")} description={t("description")} reportTTL={365}>
      <PageHeader
        onReload={fetch}
        dateRange={dateRange}
        isLoading={reportState.isLoading}
        onDateRangeSelect={onDateRangeSelect}
        onDownloadReport={downloadReport}
        reportDownloadDisabled={reportState.count === 0}
      >
        <MarketSelector
          markets={markets}
          value={market}
          onSelect={(value) => {
            setMarket(value);
            setPagination({ ...pagination, page: 1 }, "replaceIn");
          }}
        />
        <CurrencySelector value={currency} onSelect={chooseCurrency} />
        <Searcher
          onSearch={handleSearch}
          examples={["S7", "UT", "R3"]}
          loading={reportState.isLoading}
          defaultValue={search}
          placeholder={t("searcher.placeholder")}
        />
      </PageHeader>
      <TableNotification text={t("tableNotification")} />
      <Table
        bordered
        columns={columns}
        scroll={{ x: "auto" }}
        size="small"
        loading={reportState.isLoading}
        dataSource={dataSource}
        footer={() => `${t("common:table.total")}: ${reportState.count}`}
        childrenColumnName="items"
        pagination={{
          total: reportState.count,
          current: pagination.page,
          pageSize: pagination.pageSize,
          showSizeChanger: true,
          pageSizeOptions: ["10", "25", "100"],
          onChange: (page, pageSize) => {
            if (page && pageSize) {
              setPagination((currentPagination) => ({
                ...currentPagination,
                pageSize,
                page: pageSize !== currentPagination.pageSize ? 1 : page,
              }));
            }
          },
        }}
        showSorterTooltip={false}
        sortDirections={["ascend", "descend", "ascend"]}
        onChange={(_, filters, sorter: SorterResult<AuctionItem> | SorterResult<AuctionItem>[], extra) => {
          if (!Array.isArray(sorter)) {
            setOrdering(`${sorter.order === "descend" ? "-" : ""}${sorter.column?.dataIndex}`);
          }
        }}
        rowKey={(record: AuctionItem) => `auction-row-${record.iata}`}
        onExpand={(expanded, record) => expanded && onExpand(record.iata)}
        expandedRowKeys={Object.keys(reportState.itemsByIata).length === 0 ? [] : undefined}
      />
    </Page>
  );
};
